import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import ProjectAPI from "../../api/ProjectAPI";
import { headerFooterComponentListDispositionStateToApi } from "../../utils/components";

import type { HeaderFooterComponent } from "../../api/ProjectAPI.types";
import type { DispositionState } from "../../pages/platform/components/headerFooter/Disposition";

const useSaveHeaderFooterComponentListMutation = (): {
  mutateAsync: ({
    dispositionState,
    prevDispositionState,
    onSuccess,
    section,
  }: {
    dispositionState: DispositionState;
    prevDispositionState: DispositionState;
    onSuccess: () => void;
    section: "header" | "footer";
  }) => Promise<void>;
} => {
  const { t } = useTranslation("editor-platform");

  const { projectSlug } = useParams<{
    projectSlug: string;
  }>();

  const handleSuccess = (): void => {
    toast.success(t("editor-notif:saveSuccess"), {
      id: "save-header-footer-disposition-success",
    });
  };

  const handleError = (): void => {
    toast.error(t("editor-notif:saveError"), {
      id: "save-header-footer-disposition-error",
    });
  };

  const updateComponentMutation = useMutation({
    mutationFn: async (updatedComponent: HeaderFooterComponent) =>
      ProjectAPI.updateComponent({ component: updatedComponent, projectSlug }),
    onSuccess: handleSuccess,
    onError: handleError,
  });

  const createComponentMutation = useMutation({
    mutationFn: async (component: Omit<HeaderFooterComponent, "uuid">) =>
      ProjectAPI.createComponent({ component, projectSlug }),
    onSuccess: handleSuccess,
    onError: handleError,
  });

  const mutateAsync = async ({
    dispositionState,
    prevDispositionState,
    onSuccess,
    section,
  }: {
    dispositionState: DispositionState;
    prevDispositionState: DispositionState;
    onSuccess: () => void;
    section: "header" | "footer";
  }): Promise<void> => {
    const componentDetails = headerFooterComponentListDispositionStateToApi(
      dispositionState,
      prevDispositionState,
      section,
    );

    const mutations: Promise<unknown>[] = [];

    componentDetails.added.forEach((component) => {
      mutations.push(createComponentMutation.mutateAsync(component));
    });

    componentDetails.updated.forEach((component) => {
      mutations.push(updateComponentMutation.mutateAsync(component));
    });

    await Promise.all(mutations);

    onSuccess();
  };

  return {
    mutateAsync,
  };
};

export default useSaveHeaderFooterComponentListMutation;
